import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import SearchIcon from '@material-ui/icons/Search';
import Divider from '@material-ui/core/Divider';
import ProxyService from "libs/resources/proxy/ProxyService";
import { IAPIProxy } from "libs/resources/proxy/ProxyService";
import Autocomplete from '@material-ui/lab/Autocomplete';
import Paper from '@material-ui/core/Paper';
import InputAdornment from '@material-ui/core/InputAdornment';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UnifiedAPIService, { UnifiedAPI } from '../../../../services/UnifiedAPIService';
import ApiIcon from "legacy-components/icons/ApiIcon";

const useManageAPIsDrawerStyles = makeStyles(theme => ({
  drawerPaper: {
    width: '768px',
    position: 'relative',
    marginLeft: 'auto',
    boxSizing: 'border-box',
    padding: '37px 28px',
    background: '#FFFFFF',
    top: 60, // header height
    right: 0,
    height: 'calc(100% - 60px)',
    boxShadow: '-3px 4px 2px 1px rgba(0, 0, 0, 0.25)',
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontFamily: "'Open Sans'",
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: '#002D5B',
    marginBottom: '25px',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: 8,
    paddingBottom: 0,
  },
  actions: {
    borderTop: '1px solid #E0E0E0',
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '24px 0 0 0',
    background: '#fff',
    position: 'relative',
    marginTop: 24,
    marginBottom: 24,
  },
  apiRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  apiName: {
    fontWeight: 600,
    fontSize: 15,
    color: '#217CBB',
  },
  apiDesc: {
    fontSize: 13,
    color: '#555',
    marginTop: 2,
    lineHeight: 1.4,
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    borderRadius: 4,
    padding: '6px 12px',
    marginBottom: 24,
    border: '1px solid #E0E0E0',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 15,
    flex: 1,
    marginLeft: 8,
  },
  apiIcon: {
    color: '#217CBB',
    marginRight: 16,
    fontSize: 28,
  },
  removeBtn: {
    marginLeft: 8,
  },
  divider: {
    margin: '0 0 16px 0',
  },
  cancelButton: {
    marginRight: '16px',
    textTransform: 'none',
    padding: '4px 8px',
    fontSize: '14px',
    minWidth: '81px',
    fontFamily: "'Open Sans'",
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.0015em',
    border: '1px solid #252A2E',
    borderRadius: '4px',
    color: '#252A2E',
    background: '#fff',
  },
  submitButton: {
    backgroundColor: '#217CBB',
    color: '#FFFFFF',
    textTransform: 'none',
    padding: '4px 8px',
    fontSize: '14px',
    minWidth: '81px',
    height: '32px',
    borderRadius: '4px',
    fontFamily: "'Open Sans'",
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.0015em',
    boxShadow: 'none',
    '&:disabled': {
      opacity: 0.3,
    }
  },
  apiLabel: {
    fontSize: '12px',
    color: '#464B52',
    fontWeight: 700,
    marginBottom: '8px',
    marginTop: '20px',
  },
  autocompleteRoot: {
    flex: 1,
  },
  autocompletePaper: {
    background: '#fff',
    border: '1px solid #E0E0E0',
    borderRadius: 4,
    marginTop: 4,
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  searchIconStyle: {
    fontSize: '18px',
    color: '#000000',
    opacity: 0.7,
  },
  inputStyle: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: '#fff',
    fontSize: 15,
  },
  expandIcon: {
    color: '#000',
    fontSize: '16px',
    marginLeft: 8,
  },
  loadingText: {
    color: '#888',
    fontSize: 15,
    marginTop: 32,
    textAlign: 'center',
  },
  errorText: {
    color: 'red',
    fontSize: 15,
    marginTop: 32,
    textAlign: 'center',
  },
  emptyText: {
    color: '#888',
    fontSize: 15,
    marginTop: 32,
    textAlign: 'center',
  },
  removeIcon: {
    color: '#DA212C',
  },
  removeBtnWrapper: {
    display: 'inline-flex',
    verticalAlign: 'middle',
  },
}));

export interface ManageAPIsDrawerProps {
  open: boolean;
  onClose: () => void;
  productId: string; // <-- pass productId instead of apis
  /** When public, removing APIs from the product is blocked (end users may be affected). */
  productVisibility?: string;
  onRemove?: (name: string) => void;
  onUpdate: (selected: { name: string; description: string; key: string }[]) => void;
}

const ManageAPIsDrawer: React.FC<ManageAPIsDrawerProps> = ({
  open,
  onClose,
  productId,
  productVisibility,
  onRemove,
  onUpdate,
}) => {
  const classes = useManageAPIsDrawerStyles();
  const isPublicProduct =
    (productVisibility ?? '').toString().toLowerCase().trim() === 'public';
  const [publicRemoveBlockedOpen, setPublicRemoveBlockedOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [apis, setApis] = React.useState<{ name: string; description: string; key: string; assigned?: boolean }[]>([]);
  const [allApis, setAllApis] = React.useState<{ name: string; description: string; key: string }[]>([]);
  const [selected, setSelected] = React.useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [updating, setUpdating] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    // Fetch all APIs (team)
    ProxyService.forTeam(true).subscribe({
      next: (teamProxies: IAPIProxy[]) => {
        const mappedAll = teamProxies.map(proxy => ({
          key: proxy.id,
          name: proxy.apiName || proxy.id,
          description: proxy.description || ''
        }));
        setAllApis(mappedAll);
        // Fetch assigned APIs for this product
        ProxyService.forProduct(productId).subscribe({
          next: (productProxies: IAPIProxy[]) => {
            const assignedIds = new Set(productProxies.map(p => p.id));
            setApis(mappedAll.map(api => ({
              ...api,
              assigned: assignedIds.has(api.key)
            })));
            // Initialize selection from assigned property
            const initial: { [key: string]: boolean } = {};
            mappedAll.forEach(api => {
              if (assignedIds.has(api.key)) initial[api.key] = true;
            });
            setSelected(initial);
            setLoading(false);
          },
          error: () => {
            setError('Failed to load assigned APIs');
            setLoading(false);
          }
        });
      },
      error: () => {
        setError('Failed to load all APIs');
        setLoading(false);
      }
    });
  }, [open, productId, refreshKey]);

  React.useEffect(() => {
    if (!open) {
      setPublicRemoveBlockedOpen(false);
    }
  }, [open]);

  const filteredApis = apis.filter(api =>
    api.name.toLowerCase().includes(search.toLowerCase()) ||
    api.description.toLowerCase().includes(search.toLowerCase())
  );

  const selectedApisList = apis.filter(api => selected[api.key]);
  const unselectedApis = allApis.filter(api => !selected[api.key]);

  const handleToggle = (key: string) => {
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleUpdate = () => {
    // Find addApis (selected) and removeApis (unselected) - following ProductProxyView implementation
    const addApis = apis.filter(api => selected[api.key] && !api.assigned).map(api => api.name).filter(value => !!value);
    const removeApis = apis.filter(api => !selected[api.key] && api.assigned).map(api => api.name).filter(value => !!value);
    if (isPublicProduct && removeApis.length > 0) {
      setPublicRemoveBlockedOpen(true);
      return;
    }
    setUpdating(true);
    UnifiedAPIService.post(
      UnifiedAPI.parseRequestURL(UnifiedAPI.map_proxy_product, {
        productName: productId
      }),
      {
        addApis,
        removeApis
      }
    ).then(() => {
      // Step 3: Notify parent component before closing the dialog
      // This ensures the refresh happens before UI updates
      if (onUpdate) {
        onUpdate(apis.filter(api => selected[api.key]));
      }
      setUpdating(false);
      onClose(); // close the drawer after update
    }).catch((error) => {
      setUpdating(false);
    });
  };

  const handleAutocompleteChange = (event: any, value: any | null) => {
    if (value && value.key) {
      setSelected(prev => ({ ...prev, [value.key]: true }));
      // Clear the input value and also clear the Autocomplete's value
      setInputValue('');
      // If you want to clear the selected value in Autocomplete, you need to control the value prop
      setAutocompleteValue(null);
    }
  };

  // Add state to control Autocomplete's value
  const [autocompleteValue, setAutocompleteValue] = React.useState<any | null>(null);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      classes={{ paper: classes.drawerPaper }}
      ModalProps={{
        hideBackdrop: false,
      }}
      SlideProps={{
        mountOnEnter: true,
        unmountOnExit: true,
      }}
    >
      <Box>
        <Typography className={classes.title}>Manage APIs</Typography>
        <Typography className={classes.apiLabel}>Apis</Typography>
        <Box className={classes.searchBox}>
          <Autocomplete
            options={unselectedApis}
            getOptionLabel={option => option.name}
            inputValue={inputValue}
            value={autocompleteValue}
            onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
            onChange={handleAutocompleteChange}
            disabled={loading}
            PaperComponent={props => <Paper {...props} className={classes.autocompletePaper} />}
            renderInput={params => (
              <div ref={params.InputProps.ref} className={classes.inputContainer}>
                <SearchIcon className={classes.searchIconStyle} />
                <input
                  {...params.inputProps}
                  className={classes.searchInput}
                  placeholder="Search or add API..."
                  aria-label="Search APIs"
                  disabled={loading}
                />
                <InputAdornment position="end">
                  <ExpandMoreIcon className={classes.expandIcon} />
                </InputAdornment>
              </div>
            )}
            className={classes.autocompleteRoot}
          />
        </Box>
      </Box>
      <Box className={classes.content}>
        {loading && (
          <Typography className={classes.loadingText}>
            Loading APIs...
          </Typography>
        )}
        {error && (
          <Typography className={classes.errorText}>
            {error}
          </Typography>
        )}
        {!loading && !error && selectedApisList.length === 0 && (
          <Typography className={classes.emptyText}>
            No APIs selected.
          </Typography>
        )}
        {!loading && !error && selectedApisList.map((api, idx) => (
          <React.Fragment key={api.key}>
            {idx == 0  ? <Divider className={classes.divider} />: <></>}
            <Box className={classes.apiRow}>
              <Box display="flex" alignItems="flex-start">
                <ApiIcon className={classes.apiIcon} />
                <Box>
                  <Typography className={classes.apiName}>{api.name}</Typography>
                  <Typography className={classes.apiDesc}>{api.description}</Typography>
                </Box>
              </Box>
              <Box
                component="span"
                className={classes.removeBtnWrapper}
                onClick={() => {
                  if (isPublicProduct) {
                    setPublicRemoveBlockedOpen(true);
                  }
                }}
              >
                <IconButton
                  onClick={() => handleToggle(api.key)}
                  color={selected[api.key] ? 'primary' : 'default'}
                  className={classes.removeBtn}
                  aria-label={`Remove ${api.name}`}
                  disabled={isPublicProduct}
                  style={isPublicProduct ? { pointerEvents: 'none' } : undefined}
                >
                  <RemoveCircleIcon className={classes.removeIcon} />
                </IconButton>
              </Box>
            </Box>
            {idx < selectedApisList.length - 1 && <Divider className={classes.divider} />}
          </React.Fragment>
        ))}
              <Box className={classes.actions}>
        <Button
          onClick={onClose}
          variant="outlined"
          className={classes.cancelButton}
          disabled={loading|| updating}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          className={classes.submitButton}
          disabled={loading || updating}
        >
          Update
        </Button>
      </Box>
      </Box>

      <Dialog
        open={publicRemoveBlockedOpen}
        onClose={() => setPublicRemoveBlockedOpen(false)}
        aria-labelledby="public-product-remove-api-dialog-title"
      >
        <DialogTitle id="public-product-remove-api-dialog-title">
          <FormattedMessage defaultMessage="Cannot remove API" />
        </DialogTitle>
        <DialogContent>
          <Typography>
            <FormattedMessage defaultMessage="This product has public visibility. APIs cannot be removed from this product because end users may be affected." />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublicRemoveBlockedOpen(false)} color="primary" variant="contained">
            <FormattedMessage defaultMessage="OK" />
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default ManageAPIsDrawer;
